import { defineMessages, type MessageDescriptor } from 'react-intl';

import {
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
	USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
	USER_SEGMENTATION_TEAM_TYPE_DESIGN,
	USER_SEGMENTATION_TEAM_TYPE_FINANCE,
	USER_SEGMENTATION_TEAM_TYPE_HR,
	USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
	USER_SEGMENTATION_TEAM_TYPE_LEGAL,
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
	USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_SALES,
	USER_SEGMENTATION_TEAM_TYPE_SKIP,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
} from '../../models/user-segmentation';

import type { UserSegmentationTeamTypes } from '../../models/user-segmentation';

const defaultProjectNameMessages = defineMessages({
	analytics: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.analytics',
		defaultMessage: 'Analytics Service Project',
	},
	customerService: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.customerService',
		defaultMessage: 'Customer Service Project',
	},
	design: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.design',
		defaultMessage: 'Design Service Project',
	},
	fallback: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.fallback',
		defaultMessage: 'Service Project',
	},
	finance: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.finance',
		defaultMessage: 'Finance Service Project',
	},
	humanResources: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.humanResources',
		defaultMessage: 'HR Service Project',
	},
	itSupport: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.itSupport',
		defaultMessage: 'IT Service Project',
	},
	legal: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.legal',
		defaultMessage: 'Legal Service Project',
	},
	marketing: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.marketing',
		defaultMessage: 'Marketing Service Project',
	},
	operations: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.operations',
		defaultMessage: 'Operations Service Project',
	},
	productManagement: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.productManagement',
		defaultMessage: 'Product Service Project',
	},
	projectManagement: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.projectManagement',
		defaultMessage: 'Project Service Project',
	},
	sales: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.sales',
		defaultMessage: 'Sales Service Project',
	},
	software: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.defaultProjectName.software',
		defaultMessage: 'Development Requests Project',
	},
});

const defaultProjectName: Record<UserSegmentationTeamTypes, MessageDescriptor> = {
	[USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE]: defaultProjectNameMessages.customerService,
	[USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE]: defaultProjectNameMessages.analytics,
	[USER_SEGMENTATION_TEAM_TYPE_DESIGN]: defaultProjectNameMessages.design,
	[USER_SEGMENTATION_TEAM_TYPE_FINANCE]: defaultProjectNameMessages.finance,
	[USER_SEGMENTATION_TEAM_TYPE_HR]: defaultProjectNameMessages.humanResources,
	[USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: defaultProjectNameMessages.itSupport,
	[USER_SEGMENTATION_TEAM_TYPE_LEGAL]: defaultProjectNameMessages.legal,
	[USER_SEGMENTATION_TEAM_TYPE_MARKETING]: defaultProjectNameMessages.marketing,
	[USER_SEGMENTATION_TEAM_TYPE_OPERATIONS]: defaultProjectNameMessages.operations,
	[USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL]: defaultProjectNameMessages.fallback,
	[USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT]: defaultProjectNameMessages.productManagement,
	[USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT]: defaultProjectNameMessages.projectManagement,
	[USER_SEGMENTATION_TEAM_TYPE_SALES]: defaultProjectNameMessages.sales,
	[USER_SEGMENTATION_TEAM_TYPE_SKIP]: defaultProjectNameMessages.fallback,
	[USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: defaultProjectNameMessages.software,
};

const provisioningMessages = defineMessages({
	checkingOrganizationStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.checkingOrganizationStatus',
		defaultMessage: 'Checking your organization',
	},
	creatingOperationsTeamStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.creatingOperationsTeamStatus',
		defaultMessage: 'Creating your Operations team',
	},
	checkingTeamMembershipStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.checkingTeamMembershipStatus',
		defaultMessage: 'Confirming your team membership',
	},
	checkingOperationsAccessStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.checkingOperationsAccessStatus',
		defaultMessage: 'Checking Operations access',
	},
	enablingOperationsStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.enablingOperationsStatus',
		defaultMessage: 'Enabling Operations',
	},
	finishingOperationsStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.finishingOperationsStatus',
		defaultMessage: 'Finishing Operations setup',
	},
	preparingOperationsStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.preparingOperationsStatus',
		defaultMessage: 'Preparing Operations setup',
	},
	incidentProjectError: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.incidentProjectError',
		defaultMessage:
			"We couldn't set up your service space. Contact support before restarting setup.",
	},
	incidentProjectTimeout: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.incidentProjectTimeout',
		defaultMessage:
			'Your service space setup is taking longer than expected. It may still finish in the background.',
	},
	incidentOperationsError: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.incidentOperationsError',
		defaultMessage:
			"Your service space is ready, but we couldn't finish setting up Operations. You can continue in your space.",
	},
	incidentContextChanged: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.incidentContextChanged',
		defaultMessage:
			'Setup was stopped because your signup context changed. Contact support before restarting setup.',
	},
	incidentOperationsPermissionError: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.incidentOperationsPermissionError',
		defaultMessage:
			"Your service space is ready, but you don't have permission to finish Operations setup. Contact your admin for help.",
	},
	incidentOperationsTimeout: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.incidentOperationsTimeout',
		defaultMessage:
			'Your service space is ready, but Operations setup is taking longer than expected. It may still finish in the background.',
	},
	openServiceSpace: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.openServiceSpace',
		defaultMessage: 'Go to your service space',
	},
	addingRequestTypesStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.addingRequestTypesStatus',
		defaultMessage: 'Adding your request types',
	},
	almostThereStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.almostThereStatus',
		defaultMessage: 'Almost there. You can keep working in another tab while you wait...',
	},
	doneStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.doneStatus',
		defaultMessage: 'Done!',
	},
	errorStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.errorStatus',
		defaultMessage: 'Something went wrong. Please try again later.',
	},
	includes: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.includes',
		defaultMessage: 'Includes:',
	},
	settingUpHelpCenterStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.settingUpHelpCenterStatus',
		defaultMessage: 'Setting up your Help Center',
	},
	settingUpPortalStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.settingUpPortalStatus',
		defaultMessage: 'Setting up your portal',
	},
	settingUpSpaceStatus: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.settingUpSpaceStatus',
		defaultMessage: 'Setting up your space',
	},
	progressLabel: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.progressLabel',
		defaultMessage: 'Provisioning progress',
	},
	title: {
		id: 'ConfirmationPage.JsmUseCaseProvisioning.title',
		defaultMessage: 'Adding Service Collection',
	},
});

export const jsmUseCaseProvisioningMessages = {
	...provisioningMessages,
	defaultProjectName,
	defaultProjectNameFallback: defaultProjectNameMessages.fallback,
};
